/**
 * NutritionDisclaimer — shared component for Nutrition and Nutrition Scanner
 * screens. Adds:
 *   1. A visible medical disclaimer at the top ("informational, not medical
 *      advice — consult a licensed professional").
 *   2. A citations block linking to the authoritative sources our
 *      calorie / macro / hydration / meal recommendations are derived from
 *      (USDA FoodData Central, NIH ODS, ADA, WHO, Mifflin-St Jeor formula).
 *
 * Added for App Store Guideline 1.4.1 compliance (Safety: Physical Harm —
 * health information must cite sources and carry a disclaimer).
 */
import { useState } from "react";
import { ChevronDown, ChevronUp, ShieldAlert, BookOpen, ExternalLink } from "lucide-react";

const SOURCES = [
  {
    label: "USDA FoodData Central",
    detail: "Calorie and macronutrient values",
    url: "https://fdc.nal.usda.gov",
  },
  {
    label: "NIH — Dietary Reference Intakes",
    detail: "Protein, carbohydrate, fat, and water recommendations",
    url: "https://ods.od.nih.gov/HealthInformation/dietary_reference_intakes.aspx",
  },
  {
    label: "Academy of Nutrition and Dietetics",
    detail: "General healthy-eating guidance",
    url: "https://www.eatright.org",
  },
  {
    label: "WHO — Healthy Diet",
    detail: "Global healthy-diet guidelines",
    url: "https://www.who.int/news-room/fact-sheets/detail/healthy-diet",
  },
  {
    label: "Mifflin–St Jeor equation",
    detail: "Basal metabolic rate (BMR) formula used to estimate daily calories",
    url: "https://pubmed.ncbi.nlm.nih.gov/2305711/",
  },
];

export default function NutritionDisclaimer({ compact = false }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      data-testid="nutrition-disclaimer"
      style={{
        marginTop: compact ? 8 : 14,
        marginBottom: compact ? 8 : 14,
        padding: 14,
        borderRadius: 14,
        border: "1px solid rgba(178,138,62,0.28)",
        background: "rgba(178,138,62,0.05)",
      }}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <ShieldAlert size={16} strokeWidth={1.8} color="var(--gold)" style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 4 }}>
            For information only — not medical advice
          </div>
          <p style={{ fontSize: 12, lineHeight: 1.55, color: "var(--text-dim)", margin: 0 }}>
            Nova&apos;s nutrition guidance is for general educational purposes and
            is derived from public dietary references (USDA, NIH, WHO). It is
            not a substitute for professional medical, nutrition, or dietetic
            advice. Talk to a licensed healthcare provider or registered
            dietitian before making changes to your diet, especially if you
            have a medical condition, are pregnant or breastfeeding, take
            medication, or have a history of disordered eating.
          </p>

          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid="nutrition-sources-toggle"
            style={{
              marginTop: 10,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "transparent",
              border: 0,
              padding: 0,
              cursor: "pointer",
              color: "var(--gold)",
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: "0.04em",
            }}
          >
            <BookOpen size={12} strokeWidth={2} />
            {open ? "Hide sources" : "View sources & references"}
            {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>

          {open && (
            <ul
              data-testid="nutrition-sources-list"
              style={{
                listStyle: "none",
                padding: 0,
                margin: "10px 0 0 0",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              {SOURCES.map((s) => (
                <li key={s.url}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`nutrition-source-${s.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      padding: "8px 10px",
                      borderRadius: 8,
                      background: "rgba(255,255,255,0.04)",
                      textDecoration: "none",
                    }}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--text)", fontSize: 12, fontWeight: 500 }}>
                      {s.label}
                      <ExternalLink size={11} strokeWidth={1.8} />
                    </span>
                    <span style={{ color: "var(--text-faint)", fontSize: 11 }}>{s.detail}</span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
